import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';

interface UploadFormProps {
  onUploadSuccess?: (sessionId: number) => void;
}

const UploadForm: React.FC<UploadFormProps> = ({ onUploadSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [sessionName, setSessionName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files ? event.target.files[0] : null;
    if (!selectedFile) return;

    const isExcel = selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                   selectedFile.type === 'application/vnd.ms-excel' ||
                   selectedFile.name.match(/\.(xlsx|xls)$/);

    if (!isExcel) {
      setError('Only Excel files (.xlsx, .xls) are allowed');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (sessionName.trim()) {
        formData.append('sessionName', sessionName.trim());
      }

      const response = await api.post('/api/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { sessionId, message, recordCount } = response.data;
      setSuccess(`${message} - ${recordCount} records processed`);
      
      if (onUploadSuccess) {
        onUploadSuccess(sessionId);
      }

      // Navigate to the report view after a short delay
      setTimeout(() => {
        navigate(`/reports/${sessionId}`);
      }, 1500);

    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box maxWidth="md" mx="auto">
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Upload QuickBooks Reports
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          Upload 1-2 Excel files exported from QuickBooks. We'll parse Balance Sheet and Profit & Loss data.
        </Typography>

        {/* Session Name Input */}
        <TextField
          fullWidth
          label="Session Name (Optional)"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          placeholder="e.g., March 2024 Financials"
          sx={{ mb: 3 }}
          disabled={uploading}
        />

        {/* File Upload */}
        <Box sx={{ mb: 3 }}>
          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              sx={{ mb: 2 }}
            >
              Select Excel Files
            </Button>
          </label>
          
          <Typography variant="caption" display="block" color="textSecondary">
            Maximum 2 files, 10MB each. Supported formats: .xlsx, .xls
          </Typography>
        </Box>

        {/* Selected Files */}
        {file && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected File
            </Typography>
            <Chip
              icon={<FileIcon />}
              label={`${file.name} (${formatFileSize(file.size)})`}
              onDelete={() => setFile(null)}
              deleteIcon={<DeleteIcon />}
              disabled={uploading}
              sx={{ justifyContent: 'flex-start' }}
            />
          </Box>
        )}

        {/* Progress */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing files...
            </Typography>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Upload Button */}
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          size="large"
        >
          {uploading ? 'Processing...' : 'Upload & Process'}
        </Button>
      </Paper>
    </Box>
  );
};

export default UploadForm; 